/**
 * The creature inspect card (long-press a creature). Names are deterministic
 * from the voice seed — the same friend always has the same name — unless the
 * player has given it one. Traits are read off the genome, not invented.
 */
import { LIFE, GENE } from '../sim/store';
import type { InspectData } from '../sim/protocol';

const SYLLABLES = ['BO', 'MI', 'TA', 'PU', 'KE', 'LU', 'NA', 'ZO', 'RI', 'FA', 'DU', 'WE', 'PIP', 'MOP', 'TUM'];

export function nameFor(seed: number, id: number): string {
  const a = SYLLABLES[(seed * 7 + id) % SYLLABLES.length] as string;
  const b = SYLLABLES[(seed * 13 + id * 3 + 5) % SYLLABLES.length] as string;
  return a + b.toLowerCase();
}

/**
 * Clean a player-typed name: drop non-printable control chars (incl. null),
 * fold tabs/newlines/runs of spaces into single spaces, trim, cap at 16.
 * Char codes only — no escaped control literals in source (they corrupt).
 */
export function sanitizeName(raw: string): string {
  let out = '';
  let pendingSpace = false;
  for (const ch of raw) {
    const code = ch.codePointAt(0) ?? 0;
    const isSpace = ch === ' ' || code === 9 || code === 10 || code === 13;
    if (isSpace) {
      pendingSpace = out.length > 0;
      continue;
    }
    if (code < 32 || code === 127) continue; // non-printable control
    if (pendingSpace) {
      out += ' ';
      pendingSpace = false;
    }
    out += ch;
  }
  return out.slice(0, 16);
}

const LIFE_LABEL: Record<number, string> = {
  [LIFE.THRIVING]: 'THRIVING',
  [LIFE.CONTENT]: 'CONTENT',
  [LIFE.GLUM]: 'GLUM',
  [LIFE.GRIMY]: 'GRIMY',
  [LIFE.DORMANT]: 'DORMANT (SCRUB GENTLY TO WAKE)',
};

/** The two most distinctive genes become trait words. */
function traits(genes: number[]): string[] {
  const defs: [number, string, string][] = [
    [GENE.SIZE, 'BIG-BONED', 'PETITE'],
    [GENE.SPEED, 'SWIFT', 'AMBLER'],
    [GENE.SOCIABILITY, 'SOCIAL BUTTERFLY', 'LONER'],
    [GENE.CURIOSITY, 'EXPLORER', 'HOMEBODY'],
    [GENE.PLAYFULNESS, 'PLAYFUL', 'SERIOUS'],
  ];
  return defs
    .map(([g, high, low]) => ({ dev: Math.abs((genes[g] ?? 128) - 128), word: (genes[g] ?? 128) >= 128 ? high : low }))
    .sort((a, b) => b.dev - a.dev)
    .slice(0, 2)
    .filter((t) => t.dev > 12)
    .map((t) => t.word);
}

function ageLabel(ageSec: number): string {
  if (ageSec < 120) return 'A HATCHLING';
  if (ageSec < 3600) return `${Math.round(ageSec / 60)} MIN OLD`;
  if (ageSec < 172800) return `${Math.round(ageSec / 3600)} HOURS OLD`;
  return `${Math.round(ageSec / 86400)} DAYS OLD`;
}

export interface InspectOpts {
  /** player-given name, if any — overrides the procedural one */
  customName?: string;
  /** persist a new name (empty string clears it back to the default) */
  onRename?: (name: string) => void;
}

export function showInspectCard(parent: HTMLElement, d: InspectData, opts: InspectOpts = {}): void {
  const displayName = (): string => opts.customName || nameFor(d.seed, d.id);

  const wrap = document.createElement('div');
  wrap.style.cssText =
    'position:fixed;inset:0;display:grid;place-items:center;z-index:40;pointer-events:auto;background:rgba(0,0,0,0.25);';
  wrap.addEventListener('pointerdown', (e) => {
    if (e.target === wrap) wrap.remove(); // tap outside to dismiss
  });

  const win = document.createElement('div');
  win.className = 'w94-raised';
  win.style.cssText = 'min-width:240px;max-width:82vw;padding:2px;';

  const bar = document.createElement('div');
  bar.style.cssText =
    'background:linear-gradient(90deg,var(--w94-title-a),var(--w94-title-b));color:#fff;font:bold 12px/1 monospace;padding:5px 8px;letter-spacing:1px;display:flex;align-items:center;gap:6px;';
  const barName = document.createElement('span');
  barName.textContent = `${displayName()}  #${d.id + 1}`;
  barName.style.cssText = 'flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';

  const renameBtn = document.createElement('button');
  renameBtn.className = 'w94-btn';
  renameBtn.setAttribute('aria-label', 'rename');
  renameBtn.textContent = '✎'; // pencil
  renameBtn.style.cssText = 'height:24px;min-width:30px;flex:none;font-size:12px;';

  const restoreBar = (): void => {
    barName.textContent = `${displayName()}  #${d.id + 1}`;
    bar.replaceChildren(barName, renameBtn);
  };

  const beginRename = (): void => {
    const input = document.createElement('input');
    input.type = 'text';
    input.value = opts.customName || nameFor(d.seed, d.id);
    input.maxLength = 16;
    input.setAttribute('aria-label', 'creature name');
    input.style.cssText =
      'flex:1;min-width:0;font:bold 12px/1.2 monospace;padding:3px 5px;border:1px solid #000;';
    const confirm = document.createElement('button');
    confirm.className = 'w94-btn';
    confirm.textContent = 'SET';
    confirm.style.cssText = 'height:26px;min-width:42px;flex:none;';
    const commit = (): void => {
      const clean = sanitizeName(input.value);
      opts.onRename?.(clean);
      opts.customName = clean || undefined;
      restoreBar();
    };
    confirm.addEventListener('click', commit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') commit();
    });
    bar.replaceChildren(input, confirm);
    input.focus();
    input.select();
  };
  renameBtn.addEventListener('click', beginRename);

  bar.append(barName);
  if (opts.onRename) bar.append(renameBtn);

  const body = document.createElement('div');
  body.style.cssText = 'padding:10px 12px;font:11px/1.7 monospace;color:var(--w94-text);';

  const state = document.createElement('div');
  state.style.cssText = 'font-weight:bold;margin-bottom:2px;';
  state.textContent = LIFE_LABEL[d.lifeState] ?? 'CONTENT';
  const age = document.createElement('div');
  age.style.cssText = 'opacity:.75;margin-bottom:6px;';
  const t = traits(d.genes);
  age.textContent = ageLabel(d.ageSec) + (t.length > 0 ? ` · ${t.join(' · ')}` : '');
  body.append(state, age);

  const rows: [string, number][] = [
    ['FED', d.hunger / 255],
    ['CLEAN', d.clean / 255],
    ['FUN', d.fun / 255],
    ['RESTED', d.rest / 255],
    ['KNOWS YOU', Math.min(1, d.familiarity / 200)],
  ];
  for (const [label, v] of rows) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;gap:6px;margin:3px 0;';
    const name = document.createElement('span');
    name.textContent = label;
    name.style.cssText = 'width:78px;flex:none;';
    const track = document.createElement('div');
    track.className = 'w94-sunken';
    track.style.cssText = 'flex:1;height:12px;position:relative;';
    const fill = document.createElement('div');
    fill.style.cssText = `position:absolute;left:0;top:0;bottom:0;width:${Math.round(v * 100)}%;background:var(--w94-title-b);`;
    track.appendChild(fill);
    row.append(name, track);
    body.appendChild(row);
  }

  const buttonRow = document.createElement('div');
  buttonRow.style.cssText = 'display:flex;justify-content:center;padding:0 0 10px;';
  const ok = document.createElement('button');
  ok.className = 'w94-btn';
  ok.textContent = 'OK';
  ok.style.cssText = 'min-width:88px;height:32px;';
  ok.addEventListener('click', () => wrap.remove());
  buttonRow.appendChild(ok);

  win.append(bar, body, buttonRow);
  wrap.appendChild(win);
  parent.appendChild(wrap);
}
